import React, { useState, useEffect, useMemo, useRef, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button";
import { useAuth } from "../contexts/AuthContext";
import { useProfileInfo } from "../contexts/ProfileInfoContext";
import { supabase } from "../supabase";
import useSnackbarNotification from "../hooks/useSnackbarNotification";
import { ensureListingCreditAvailable, NO_LISTING_CREDITS_ERROR } from "../services/listingPurchases";
import { specialties } from "../constants/specialties";
import useListingCredits from "../hooks/useListingCredits";
import { getTeamSeatLimit } from "../constants/teamSeats";
import { sendTeamInviteEmail } from "../services/emailService";
import { steps as EMPLOYER_STEPS } from "../components/employer/onboarding/constants";

// Lazy load step components for better performance
const EmployerOnboardingStep0 = lazy(() => import("../components/employer/onboarding/EmployerOnboardingStep0"));
const EmployerOnboardingStep1 = lazy(() => import("../components/employer/onboarding/EmployerOnboardingStep1"));
const EmployerOnboardingStep2 = lazy(() => import("../components/employer/onboarding/EmployerOnboardingStep2"));
const EmployerOnboardingStep3 = lazy(() => import("../components/employer/onboarding/EmployerOnboardingStep3"));

const EmployerOnboardingPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { profileInfo, updateProfileInfo } = useProfileInfo();
    const { showSuccess, showError } = useSnackbarNotification();
    const { credits, loading: creditsLoading } = useListingCredits();
    const [currentStep, setCurrentStep] = useState(0);
    const [logoUploading, setLogoUploading] = useState(false);
    // Intercept browser back to step through onboarding instead of leaving
    const stepRef = useRef(currentStep);
    useEffect(() => {
        stepRef.current = currentStep;
    }, [currentStep]);
    useEffect(() => {
        const onPopState = async (e) => {
            e.preventDefault();
            if (stepRef.current === 0) {
                // Sign out user before redirecting to login
                await supabase.auth.signOut();
                navigate("/login", { replace: true });
            } else {
                setCurrentStep((s) => Math.max(0, s - 1));
                window.history.pushState(null, "", window.location.href);
            }
        };
        window.history.pushState(null, "", window.location.href);
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, [navigate]);


    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        workEmail: "",
        companyName: "",
        companyUrl: "",
        industry: "",
        about: "",
        logoFileName: "",
        logoUrl: "",
        jobTitle: "",
        specialty: "",
         jobLocation: "",
        employmentType: "",
        workPreference: "",
        roleDescription: "",
        preferredCandidate: "",
        dutiesAndResponsibilities: "",
        country: "",
        countryCode: "",
        province: "",
        languageRequirement: [],
        locationOrLicenceRestrictions: null, // "yes", "no", or null
        locationOrLicenceRestrictionsText: "",
        qualifications: "",
        requiresLicense: "",
        sponsorship: "",
        // Requirements step fields
        requirementsSpecialty: "",
        experienceLevel: "",
        yearsOfExperience: "",
        budgetForRole: "",
        salaryCurrency: "ZAR",
        salaryPeriod: "yearly",
        requiredSkills: [],
        otherPreferences: "",
        screeningQuestions: false,
        driversLicenseRequired: false,
        driversLicenseAnswer: null, // "yes", "no", or null
        visaSponsorshipOffered: false,
        visaSponsorshipAnswer: null, // "yes", "no", or null
        screeningQuestionText: "",
        screeningQuestionOptions: [],
    });

    // Local state for each preference form
    const [currentQuestion, setCurrentQuestion] = useState("");
    const [currentQuestionType, setCurrentQuestionType] = useState("multiple-choice"); // "multiple-choice" or "text"
    const [currentOptions, setCurrentOptions] = useState([""]);
    const [screeningQuestionsList, setScreeningQuestionsList] = useState([]);
    
    const [driversLicenseAnswer, setDriversLicenseAnswer] = useState(null); // null, "yes", or "no"
    const [visaSponsorshipAnswer, setVisaSponsorshipAnswer] = useState(null); // null, "yes", or "no"
    const [isCustomSpecialty, setIsCustomSpecialty] = useState(false); // For "Other" specialty option

    // Team members state
    const [teamMembers, setTeamMembers] = useState([
        {
            firstName: "Bob",
            lastName: "",
            workEmail: "",
            roles: []
        }
    ]);
    const metadataPlanLabel = user?.user_metadata?.subscription_plan || "Free Starter";
    const creditsOrMetadata = credits ?? { isFreePlan: metadataPlanLabel === "Free Starter" };
    const currentPlanLabel = creditsOrMetadata.isFreePlan ? "Free Starter" : "Sponsored Jobs";
    const teamSeatLimit = getTeamSeatLimit(creditsOrMetadata);
    const countedTeamMembers = useMemo(
        () => teamMembers.filter(member => member.workEmail && member.firstName),
        [teamMembers]
    );
    const countedTeamMembersCount = countedTeamMembers.length;
    const hasReachedSeatLimit = countedTeamMembersCount >= teamSeatLimit;
    
    // Auto-dismiss upgrade message after 5 seconds
    useEffect(() => {
        if (hasReachedSeatLimit) {
            setShowUpgradeMessage(true);
            const timer = setTimeout(() => {
                setShowUpgradeMessage(false);
            }, 5000);
            return () => clearTimeout(timer);
        } else {
            setShowUpgradeMessage(false);
        }
    }, [hasReachedSeatLimit]);
    
    const [skillInput, setSkillInput] = useState("");
    const [socialMediaInputs, setSocialMediaInputs] = useState([{ platform: "", url: "" }]);
    const [showUpgradeMessage, setShowUpgradeMessage] = useState(false);
    
    // Track if initial load is complete to prevent saving empty state and re-loading from metadata
    const hasInitialLoadCompletedRef = useRef(false);
    const hasLoadedFromMetadataRef = useRef(false);
    
    // Invite team member modal state
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteForm, setInviteForm] = useState({
        firstName: "",
        lastName: "",
        workEmail: "",
        role: "member"
    });

    // Edit team member modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingMemberIndex, setEditingMemberIndex] = useState(null);
    const [editForm, setEditForm] = useState({
        firstName: "",
        lastName: "",
        workEmail: "",
        role: "member"
    });

    // Team member functions
    const addTeamMember = () => {
        if (hasReachedSeatLimit) {
            showError(
                `Your ${currentPlanLabel} plan includes ${teamSeatLimit} team seat${teamSeatLimit === 1 ? "" : "s"}. Remove someone or upgrade to add more.`
            );
            return;
        }
        setShowInviteModal(true);
    };

    const openEditModal = (memberIndex) => {
        const member = teamMembers[memberIndex];
        setEditingMemberIndex(memberIndex);
        // Handle backward compatibility: if roles is an array, take first one; if it's a string, use it; otherwise default to 'member'
        let roleValue = "member";
        if (Array.isArray(member.roles) && member.roles.length > 0) {
            roleValue = member.roles[0].toLowerCase();
        } else if (typeof member.role === 'string') {
            roleValue = member.role.toLowerCase();
        }
        // Ensure it's one of the valid options
        if (!['admin', 'member', 'read-only'].includes(roleValue)) {
            roleValue = 'member';
        }
        setEditForm({
            firstName: member.firstName || member.profileFirstName || "",
            lastName: member.lastName || member.profileLastName || "",
            workEmail: member.workEmail || "",
            role: roleValue
        });
        setShowEditModal(true);
    };

    const closeEditModal = () => {
        setShowEditModal(false);
        setEditingMemberIndex(null);
        setEditForm({
            firstName: "",
            lastName: "",
            workEmail: "",
            role: "member"
        });
    };

    const handleInviteSubmit = async () => {
        if (hasReachedSeatLimit) {
            showError(
                `You have already used all ${teamSeatLimit} team seat${teamSeatLimit === 1 ? "" : "s"} available on the ${currentPlanLabel} plan.`
            );
            return;
        }
        if (!inviteForm.firstName || !inviteForm.workEmail) {
            showError("Please fill in required fields");
            return;
        }

        // Validate email if provided
        if (inviteForm.workEmail) {
            try {
                const { data, error } = await supabase
                    .from('users_profiles')
                    .select('profileid, email, role, profile_image_url, firstname, lastname')
                    .eq('email', inviteForm.workEmail.toLowerCase())
                    .single();

                if (error && error.code !== 'PGRST116') {
                    console.error('Error validating email:', error);
                    showError("Error validating email");
                    return;
                }

                const newMember = {
                    firstName: inviteForm.firstName,
                    lastName: inviteForm.lastName,
                    workEmail: inviteForm.workEmail,
                    role: inviteForm.role,
                    roles: [inviteForm.role], // Keep for backward compatibility
                    isValidated: true, // Allow inviting members even if they have not yet signed up
                    validationError: "",
                    userProfileId: data?.profileid || null,
                    userType: data?.role || null,
                    profileImageUrl: data?.profile_image_url || null,
                    profileFirstName: data?.firstname || null,
                    profileLastName: data?.lastname || null,
                };

                // Remove placeholder members (those without email) when adding a real member
                const filteredMembers = teamMembers.filter(member => member.workEmail || member.firstName !== "Bob");
                setTeamMembers([...filteredMembers, newMember]);
                setShowInviteModal(false);
                setInviteForm({ firstName: "", lastName: "", workEmail: "", role: "member" });
                showSuccess("Team member added successfully");
                
                // Send team invite email
                try {
                    const inviterName = `${profileInfo?.firstname || ''} ${profileInfo?.lastname || ''}`.trim() || user?.email?.split('@')[0] || 'Team Admin';
                    const companyName = form?.companyName || user?.user_metadata?.company_name || 'your company';
                    const inviteeName = `${inviteForm.firstName} ${inviteForm.lastName || ''}`.trim() || inviteForm.workEmail.split('@')[0];
                    const roleLabel = inviteForm.role === 'admin' ? 'Admin' : inviteForm.role === 'read-only' ? 'Read-Only' : 'Member';
                    // Generate invite link - you may want to create a proper invite token system later
                    const inviteLink = `https://doctorsjobs.io/signup?invite=${encodeURIComponent(inviteForm.workEmail)}`;
                    
                    await sendTeamInviteEmail(
                        inviteForm.workEmail,
                        inviteeName,
                        inviterName,
                        companyName,
                        roleLabel,
                        inviteLink
                    );
                } catch (emailError) {
                    console.error('Failed to send team invite email:', emailError);
                    // Don't fail the invite if email fails
                }
            } catch (error) {
                console.error('Error validating email:', error);
                showError("Error validating email");
            }
        } else {
            const newMember = {
                firstName: inviteForm.firstName,
                lastName: inviteForm.lastName,
                workEmail: inviteForm.workEmail,
                role: inviteForm.role,
                roles: [inviteForm.role], // Keep for backward compatibility
                isValidated: false,
                validationError: "",
                userProfileId: null,
                userType: null,
                profileImageUrl: null,
                profileFirstName: null,
                profileLastName: null,
            };
            // Remove placeholder members (those without email) when adding a real member
            const filteredMembers = teamMembers.filter(member => member.workEmail || member.firstName !== "Bob");
            setTeamMembers([...filteredMembers, newMember]);
            setShowInviteModal(false);
            setInviteForm({ firstName: "", lastName: "", workEmail: "", role: "member" });
            showSuccess("Team member added successfully");
            
            // Send team invite email
            try {
                const inviterName = `${profileInfo?.firstname || ''} ${profileInfo?.lastname || ''}`.trim() || user?.email?.split('@')[0] || 'Team Admin';
                const companyName = form?.companyName || user?.user_metadata?.company_name || 'your company';
                const inviteeName = `${inviteForm.firstName} ${inviteForm.lastName || ''}`.trim() || inviteForm.workEmail.split('@')[0];
                const roleLabel = inviteForm.role === 'admin' ? 'Admin' : inviteForm.role === 'read-only' ? 'Read-Only' : 'Member';
                // Generate invite link - you may want to create a proper invite token system later
                const inviteLink = `https://doctorsjobs.io/signup?invite=${encodeURIComponent(inviteForm.workEmail)}`;
                
                await sendTeamInviteEmail(
                    inviteForm.workEmail,
                    inviteeName,
                    inviterName,
                    companyName,
                    roleLabel,
                    inviteLink
                );
            } catch (emailError) {
                console.error('Failed to send team invite email:', emailError);
                // Don't fail the invite if email fails
            }
        }
    };


    const updateTeamMember = (index, field, value) => {
        const updated = [...teamMembers];
        updated[index][field] = value;
        setTeamMembers(updated);
    };

    const removeTeamMember = (index) => {
        // Confirm deletion
        if (window.confirm("Are you sure you want to delete this team member?")) {
            const updatedMembers = teamMembers.filter((_, i) => i !== index);
            setTeamMembers(updatedMembers);
            showSuccess("Team member removed");
        }
    };

    const handleEditSubmit = () => {
        if (!editForm.firstName || !editForm.workEmail) {
            showError("Please fill in required fields");
            return;
        }

        // Update the team member
        const updated = [...teamMembers];
        updated[editingMemberIndex] = {
            ...updated[editingMemberIndex],
            firstName: editForm.firstName,
            lastName: editForm.lastName,
            workEmail: editForm.workEmail,
            role: editForm.role,
            roles: [editForm.role] // Keep for backward compatibility
        };
        setTeamMembers(updated);
        
        closeEditModal();
        showSuccess("Team member updated successfully");
    };

    const handleLogoUpload = async (file) => {
        if (!file || !user) return;
        try {
            setLogoUploading(true);
            // Store in profile images bucket (same as profile images)
            const bucket = process.env.REACT_APP_PROFILE_IMAGES_BUCKET || "profile-images";
            const filePath = `${user.id}/company_logo_${Date.now()}_${file.name}`;
            const { error: uploadError } = await supabase.storage.from(bucket).upload(filePath, file, {
                upsert: true,
                contentType: file.type || "image/jpeg",
            });
            if (uploadError) throw uploadError;

            // Get public URL
            const { data: publicData } = supabase.storage.from(bucket).getPublicUrl(filePath);
            const publicUrl = publicData?.publicUrl || "";

            // Save reference in user metadata
            const { error: metaError } = await supabase.auth.updateUser({
                data: {
                    company_logo_url: publicUrl,
                    company_logo_path: `${bucket}/${filePath}`,
                    company_logo_filename: file.name,
                },
            });
            if (metaError) throw metaError;

            // Update form with logo info
            setForm((p) => ({ 
                ...p, 
                logoFileName: file.name,
                logoUrl: publicUrl 
            }));
            showSuccess("Company logo uploaded successfully");
        } catch (e) {
            console.error("Logo upload error", e);
            showError(e.message || "Failed to upload company logo");
        } finally {
            setLogoUploading(false);
        }
    };

    // Skills functions
    const addSkill = (skill) => {
        if (skill.trim()) {
            setForm((p) => ({
                ...p,
                requiredSkills: [...p.requiredSkills, skill.trim()]
            }));
            setSkillInput("");
        }
    };

    const removeSkill = (skillIndex) => {
        setForm((p) => ({
            ...p,
            requiredSkills: p.requiredSkills.filter((_, i) => i !== skillIndex)
        }));
    };

    // Generic option management functions
    const addOption = (options, setOptions) => {
        setOptions([...options, ""]);
    };

    const updateOption = (options, setOptions, index, value) => {
        const updated = [...options];
        updated[index] = value;
        setOptions(updated);
    };

    const removeOption = (options, setOptions, index) => {
        setOptions(options.filter((_, i) => i !== index));
    };

    // Screening question functions
    const addScreeningOption = () => addOption(currentOptions, setCurrentOptions);
    const updateScreeningOption = (index, value) => updateOption(currentOptions, setCurrentOptions, index, value);
    const removeScreeningOption = (index) => removeOption(currentOptions, setCurrentOptions, index);

    const addScreeningQuestion = () => {
        if (currentQuestion.trim()) {
            if (currentQuestionType === "text") {
                // Long answer question - no options needed
                const question = {
                    question: currentQuestion.trim(),
                    type: "text"
                };
                setScreeningQuestionsList([...screeningQuestionsList, question]);
                setCurrentQuestion("");
                setCurrentQuestionType("multiple-choice");
                setCurrentOptions([""]);
            } else {
                // Multi-choice question - require at least one option
                if (currentOptions.some(opt => opt.trim())) {
                    const question = {
                        question: currentQuestion.trim(),
                        type: "multiple-choice",
                        options: currentOptions.filter(opt => opt.trim())
                    };
                    setScreeningQuestionsList([...screeningQuestionsList, question]);
                    setCurrentQuestion("");
                    setCurrentQuestionType("multiple-choice");
                    setCurrentOptions([""]);
                }
            }
        }
    };

    const removeScreeningQuestion = (index) => {
        setScreeningQuestionsList(screeningQuestionsList.filter((_, i) => i !== index));
    };

    // Specialty selection handlers
    const handleSpecialtySelect = (value) => {
        if (value === "Other") {
            setIsCustomSpecialty(true);
            setForm((p) => ({ ...p, requirementsSpecialty: "" }));
            return;
        }
        setIsCustomSpecialty(false);
        setForm((p) => ({ ...p, requirementsSpecialty: value }));
    };

    const resetSpecialtyToSelect = () => {
        setIsCustomSpecialty(false);
        setForm((p) => ({ ...p, requirementsSpecialty: "" }));
    };

    // Toggle helpers now allow stacking multiple preference sections
    const handleSelectScreening = () => {
        const newValue = !form.screeningQuestions;
        setForm((p) => ({
            ...p,
            screeningQuestions: newValue,
        }));
        if (!newValue) {
            setCurrentQuestion("");
            setCurrentQuestionType("multiple-choice");
            setCurrentOptions([""]);
            setScreeningQuestionsList([]);
        }
    };

    const handleSelectDriversLicense = () => {
        const newValue = !form.driversLicenseRequired;
        setForm((p) => ({
            ...p,
            driversLicenseRequired: newValue,
            ...(newValue ? {} : { driversLicenseAnswer: null }),
        }));
        if (!newValue) {
            setDriversLicenseAnswer(null);
        }
    };

    const handleSelectVisaSponsorship = () => {
        const newValue = !form.visaSponsorshipOffered;
        setForm((p) => ({
            ...p,
            visaSponsorshipOffered: newValue,
            ...(newValue ? {} : { visaSponsorshipAnswer: null }),
        }));
        if (!newValue) {
            setVisaSponsorshipAnswer(null);
        }
    };

    // Social media functions
    const addSocialMediaField = () => {
        setSocialMediaInputs([...socialMediaInputs, { platform: "", url: "" }]);
    };

    const updateSocialMediaInput = (index, field, value) => {
        const updated = [...socialMediaInputs];
        updated[index][field] = value;
        setSocialMediaInputs(updated);
    };

    const removeSocialMediaField = (index) => {
        if (socialMediaInputs.length > 1) {
            const updated = socialMediaInputs.filter((_, i) => i !== index);
            setSocialMediaInputs(updated);
        }
    };

    // Preload existing data from user metadata
    // Redirect to dashboard if onboarding is already completed
    // Database uses lowercase 'onboardingcompleted'
    useEffect(() => {
        if (profileInfo?.onboardingcompleted === true) {
            navigate("/employer/dashboard", { replace: true });
        }
    }, [profileInfo?.onboardingcompleted, navigate]);

    // Load from metadata only ONCE on initial mount (not when metadata changes after saving)
    useEffect(() => {
        // Only load from metadata once to prevent overwriting in-progress form data
        if (hasLoadedFromMetadataRef.current) return;
        
        // Mark as loaded even if employer_profile is empty - prevents loading later when it gets created
        hasLoadedFromMetadataRef.current = true;
        
        if (!user?.user_metadata?.employer_profile) return;
        
        const employerData = user.user_metadata.employer_profile;
        setForm(prev => ({
            ...prev,
            // Company info
            firstName: employerData.companyInfo?.firstName || "",
            lastName: employerData.companyInfo?.lastName || "",
            workEmail: employerData.companyInfo?.workEmail || "",
            companyName: employerData.companyInfo?.companyName || "",
            companyUrl: employerData.companyInfo?.companyUrl || "",
            industry: employerData.companyInfo?.industry || "",
            about: employerData.companyInfo?.about || "",
            logoFileName: employerData.companyInfo?.logoFileName || user.user_metadata?.company_logo_filename || "",
            logoUrl: user.user_metadata?.company_logo_url || "",
            // Job description (step 2 saves to jobDescription)
            jobTitle: employerData.jobDescription?.jobTitle || "",
            specialty: employerData.jobDescription?.specialty || "",
            jobLocation: employerData.jobDescription?.jobLocation || "",
            employmentType: employerData.jobDescription?.employmentType || "",
            workPreference: employerData.jobDescription?.workPreference || "",
            roleDescription: employerData.jobDescription?.roleDescription || "",
            preferredCandidate: employerData.jobDescription?.preferredCandidate || "",
            dutiesAndResponsibilities: employerData.jobDescription?.dutiesAndResponsibilities || "",
            country: employerData.jobDescription?.country || "",
            countryCode: employerData.jobDescription?.countryCode || "",
            province: employerData.jobDescription?.province || "",
            languageRequirement: employerData.jobDescription?.languageRequirement || [],
            locationOrLicenceRestrictions: employerData.jobDescription?.locationOrLicenceRestrictions || null,
            locationOrLicenceRestrictionsText: employerData.jobDescription?.locationOrLicenceRestrictionsText || "",
            qualifications: employerData.jobDescription?.qualifications || "",
            requiresLicense: employerData.jobDescription?.requiresLicense || "",
            sponsorship: employerData.jobDescription?.sponsorship || "",
            // Requirements
            experienceRequirements: employerData.requirements?.experienceRequirements || "",
            benefits: employerData.requirements?.benefits || "",
            requirementsSpecialty: employerData.requirements?.requirementsSpecialty || "",
            experienceLevel: employerData.requirements?.experienceLevel || "",
            yearsOfExperience: employerData.requirements?.yearsOfExperience || "",
            budgetForRole: employerData.requirements?.budgetForRole || employerData.requirements?.salaryRange || "",
            salaryCurrency: employerData.requirements?.salaryCurrency || employerData.requirements?.salary_currency || "ZAR",
            salaryPeriod: employerData.requirements?.salaryPeriod || employerData.requirements?.salary_period || "yearly",
            requiredSkills: employerData.requirements?.requiredSkills || [],
            otherPreferences: employerData.requirements?.otherPreferences || "",
            screeningQuestions: employerData.requirements?.screeningQuestions || false,
            driversLicenseRequired: employerData.requirements?.driversLicenseRequired || false,
            driversLicenseAnswer: employerData.requirements?.driversLicenseAnswer || null,
            visaSponsorshipOffered: employerData.requirements?.visaSponsorshipOffered || false,
            visaSponsorshipAnswer: employerData.requirements?.visaSponsorshipAnswer || null,
        }));
        
        // Load screening questions list
        if (employerData.requirements?.screeningQuestionsList) {
            setScreeningQuestionsList(employerData.requirements.screeningQuestionsList);
        }
        
        // Load answer states for Yes/No questions
        setDriversLicenseAnswer(employerData.requirements?.driversLicenseAnswer || null);
        setVisaSponsorshipAnswer(employerData.requirements?.visaSponsorshipAnswer || null);
        
        // Load team members if they exist
        if (employerData.teamMembers && employerData.teamMembers.length > 0) {
            const normalizedMembers = employerData.teamMembers.map(member => {
                const hasValidationError = Boolean(member?.validationError);
                const hasEmail = Boolean(member?.workEmail);

                return {
                    ...member,
                    validationError: member?.validationError || "",
                    // Treat legacy entries without errors as valid as long as an email exists
                    isValidated: hasValidationError
                        ? false
                        : hasEmail
                            ? true
                            : Boolean(member?.isValidated),
                    // Ensure roles array always exists to keep later logic simple
                    roles: Array.isArray(member?.roles)
                        ? member.roles
                        : member?.role
                            ? [member.role]
                            : ['member'],
                };
            });
            setTeamMembers(normalizedMembers);
        }
    }, [
        user?.id,
        user?.user_metadata?.company_logo_filename,
        user?.user_metadata?.company_logo_url,
        user?.user_metadata?.employer_profile
    ]);

    // Load draft from localStorage on mount (after user metadata is loaded)
    // This runs after the metadata loading useEffect, so we merge draft data to preserve in-progress work
    useEffect(() => {
        if (!user?.id) return;
        
        const draftKey = `employer_onboarding_draft_${user.id}`;
        const savedDraft = localStorage.getItem(draftKey);
        
        if (savedDraft) {
            try {
                const draft = JSON.parse(savedDraft);
                
                // Load draft data to restore in-progress work
                // IMPORTANT: Merge draft.form with existing form to preserve new fields not in old drafts
                if (draft.currentStep !== undefined) setCurrentStep(draft.currentStep);
                if (draft.form) {
                    setForm(prev => ({
                        ...prev,  // Keep defaults and any new fields
                        ...draft.form  // Override with saved draft values
                    }));
                }
                if (draft.currentQuestion !== undefined) setCurrentQuestion(draft.currentQuestion);
                if (draft.currentQuestionType !== undefined) setCurrentQuestionType(draft.currentQuestionType);
                if (draft.currentOptions) setCurrentOptions(draft.currentOptions);
                if (draft.screeningQuestionsList) setScreeningQuestionsList(draft.screeningQuestionsList);
                if (draft.driversLicenseAnswer !== undefined) setDriversLicenseAnswer(draft.driversLicenseAnswer);
                if (draft.visaSponsorshipAnswer !== undefined) setVisaSponsorshipAnswer(draft.visaSponsorshipAnswer);
                if (draft.isCustomSpecialty !== undefined) setIsCustomSpecialty(draft.isCustomSpecialty);
                if (draft.teamMembers) setTeamMembers(draft.teamMembers);
                if (draft.skillInput !== undefined) setSkillInput(draft.skillInput);
                if (draft.socialMediaInputs) setSocialMediaInputs(draft.socialMediaInputs);
            } catch (err) {
                console.error("Failed to load employer onboarding draft:", err);
            }
        }
        
        // Mark initial load as complete - this prevents the save effect from overwriting with empty state
        // Use requestAnimationFrame to ensure state updates are fully applied before enabling saves
        requestAnimationFrame(() => {
            hasInitialLoadCompletedRef.current = true;
        });
    }, [user?.id]);

    // Auto-save draft to localStorage whenever state changes
    useEffect(() => {
        // Don't save until initial load is complete to prevent overwriting draft with empty state
        if (!user?.id || !hasInitialLoadCompletedRef.current) return;
        
        const draftKey = `employer_onboarding_draft_${user.id}`;
        const draft = {
            currentStep,
            form,
            currentQuestion,
            currentQuestionType,
            currentOptions,
            screeningQuestionsList,
            driversLicenseAnswer,
            visaSponsorshipAnswer,
            isCustomSpecialty,
            teamMembers,
            skillInput,
            socialMediaInputs,
        };
        
        try {
            localStorage.setItem(draftKey, JSON.stringify(draft));
        } catch (err) {
            console.error("Failed to save employer onboarding draft:", err);
        }
    }, [
        user?.id,
        currentStep,
        form,
        currentQuestion,
        currentQuestionType,
        currentOptions,
        screeningQuestionsList,
        driversLicenseAnswer,
        visaSponsorshipAnswer,
        isCustomSpecialty,
        teamMembers,
        skillInput,
        socialMediaInputs,
    ]);

    const goBack = async () => {
        if (currentStep === 0) {
            // Sign out user before redirecting to login
            await supabase.auth.signOut();
            navigate("/login", { replace: true });
            return;
        }
        setCurrentStep((s) => Math.max(0, s - 1));
    };

    const handleSkip = async () => {
        // If on the last step, complete onboarding
        if (currentStep === EMPLOYER_STEPS.length - 1) {
            try {
                // Mark onboarding as completed
                await updateProfileInfo({
                    onboardingCompleted: true,
                    onboardingStep: EMPLOYER_STEPS.length - 1
                });

                // Clear draft from localStorage since onboarding is complete
                if (user?.id) {
                    const draftKey = `employer_onboarding_draft_${user.id}`;
                    localStorage.removeItem(draftKey);
                }

                navigate("/employer/dashboard");
            } catch (error) {
                console.error("Error completing onboarding:", error);
                showError("Failed to complete onboarding. Please try again.");
            }
            return;
        }
        // Otherwise, move to the next step
        setCurrentStep((s) => Math.min(EMPLOYER_STEPS.length - 1, s + 1));
    };

    const goNext = async () => {
        try {
            // Save current step data to user metadata
            if (currentStep === 0) {
                // Sync social media links from inputs
                const validSocialMediaLinks = socialMediaInputs
                    .filter(input => input.platform && input.url)
                    .map(input => ({ platform: input.platform, url: input.url }));
                
                // Save company information
                const employerProfile = {
                    ...(user?.user_metadata?.employer_profile || {}),
                    companyInfo: {
                        firstName: form.firstName,
                        lastName: form.lastName,
                        workEmail: form.workEmail,
                        companyName: form.companyName,
                        companyUrl: form.companyUrl,
                        industry: form.industry,
                        about: form.about,
                        logoFileName: form.logoFileName,
                        logoUrl: form.logoUrl,
                        socialMediaLinks: validSocialMediaLinks,
                    }
                };
                
                const { error } = await supabase.auth.updateUser({
                    data: {
                        employer_profile: employerProfile,
                    },
                });
                if (error) throw error;
                showSuccess("Company information saved");
                
            } else if (currentStep === 1) {
                // Save team members information to database
                let teamMembersSaved = false;
                
                if (teamMembers.length > 0) {
                    // Filter out invalid team members
                    const validTeamMembers = teamMembers.filter(member => 
                        member.isValidated && member.workEmail && member.firstName
                    );

                    if (validTeamMembers.length > 0) {
                        // Get existing team members to check for duplicates
                        const { data: existingMembers, error: fetchError } = await supabase
                            .from('team_members')
                            .select('work_email')
                            .eq('employer_id', user.id);

                        if (fetchError) throw fetchError;

                        const existingEmails = new Set((existingMembers || []).map(m => m.work_email?.toLowerCase()));
                        
                        // Filter out team members that already exist in database (by email)
                        const newTeamMembers = validTeamMembers.filter(member => 
                            !existingEmails.has(member.workEmail?.toLowerCase())
                        );

                        // If all members already exist, skip insert but allow continuation
                        if (newTeamMembers.length === 0) {
                            teamMembersSaved = true; // Mark as saved to avoid duplicate message
                        } else {
                            // Backend validation: Check existing team members count
                            const existingCount = existingMembers?.length || 0;
                            const currentSeatLimit = getTeamSeatLimit(creditsOrMetadata);
                            const totalAfterInsert = existingCount + newTeamMembers.length;

                            if (totalAfterInsert > currentSeatLimit) {
                                const availableSeats = currentSeatLimit - existingCount;
                                showError(
                                    `Your ${currentPlanLabel} plan allows ${currentSeatLimit} team seat${currentSeatLimit === 1 ? "" : "s"}. ` +
                                    `You already have ${existingCount} team member${existingCount === 1 ? "" : "s"} in the database. ` +
                                    `You can only add ${availableSeats} more seat${availableSeats === 1 ? "" : "s"}. ` +
                                    `Please remove ${newTeamMembers.length - availableSeats} team member${newTeamMembers.length - availableSeats === 1 ? "" : "s"} from your list or upgrade your plan.`
                                );
                                return;
                            }

                            const teamMembersData = newTeamMembers.map(member => {
                                // Use new role field if available, otherwise fall back to roles array for backward compatibility
                                let rolesArray = [];
                                if (typeof member.role === 'string') {
                                    rolesArray = [member.role];
                                } else if (Array.isArray(member.roles)) {
                                    rolesArray = member.roles;
                                }
                                
                                return {
                                    employer_id: user.id,
                                    first_name: member.firstName,
                                    last_name: member.lastName,
                                    work_email: member.workEmail,
                                    roles: rolesArray,
                                    user_profile_id: member.userProfileId,
                                    user_type: member.userType
                                };
                            });

                            const { error: teamError } = await supabase
                                .from('team_members')
                                .insert(teamMembersData);

                            if (teamError) throw teamError;
                            teamMembersSaved = true; // Mark as saved after successful insert
                        }
                    }

                    // Check if there are invalid team members (only block when there is an actual validation error)
                    const invalidMembers = teamMembers.filter(member => 
                        member.workEmail && member.validationError
                    );
                    
                    if (invalidMembers.length > 0) {
                        showError(`Please fix ${invalidMembers.length} invalid team member(s) before continuing`);
                        return;
                    }
                }

                // Also save to user metadata for profile info
                const employerProfile = {
                    ...(user?.user_metadata?.employer_profile || {}),
                    teamMembers: teamMembers
                };
                
                const { error } = await supabase.auth.updateUser({
                    data: {
                        employer_profile: employerProfile,
                    },
                });
                if (error) throw error;
                
                // Only show success message if we haven't already shown one
                if (teamMembersSaved) {
                    showSuccess("Team members saved");
                }
                
            } else if (currentStep === 2) {
                // Validate required fields for job description
                if (!form.jobTitle || !form.jobTitle.trim()) {
                    showError("Job Title is required");
                    return;
                }
                
                // Save job description information
                const employerProfile = {
                    ...(user?.user_metadata?.employer_profile || {}),
                    jobDescription: {
                        jobTitle: form.jobTitle,
                        specialty: form.specialty,
                        jobLocation: form.jobLocation,
                        employmentType: form.employmentType,
                        workPreference: form.workPreference,
                        roleDescription: form.roleDescription,
                        preferredCandidate: form.preferredCandidate,
                        dutiesAndResponsibilities: form.dutiesAndResponsibilities,
                        country: form.country,
                        countryCode: form.countryCode,
                        province: form.province,
                        languageRequirement: form.languageRequirement,
                        locationOrLicenceRestrictions: form.locationOrLicenceRestrictions,
                        locationOrLicenceRestrictionsText: form.locationOrLicenceRestrictionsText,
                        about: form.about,
                    }
                };
                
                const { error } = await supabase.auth.updateUser({
                    data: {
                        employer_profile: employerProfile,
                    },
                });
                if (error) throw error;
                showSuccess("Job description saved");
                
            } else if (currentStep === 3) {
                // Sync social media links from inputs
                const validSocialMediaLinks = socialMediaInputs
                    .filter(input => input.platform && input.url)
                    .map(input => ({ platform: input.platform, url: input.url }));
                
                try {
                    await ensureListingCreditAvailable(user.id, user.email);
                } catch (creditError) {
                    if (creditError?.code === NO_LISTING_CREDITS_ERROR) {
                        navigate("/employer/jobs", { state: { showPromotionModal: true } });
                        return;
                    }
                    throw creditError;
                }
                
                // Ensure screening questions is disabled if no questions were added
                // This prevents any errors if user enabled screening questions but didn't add any questions
                const effectiveScreeningQuestions = form.screeningQuestions && screeningQuestionsList.length > 0;
                
                // Build location string from country and province
                const locationParts = [];
                if (form.province) locationParts.push(form.province);
                if (form.country) locationParts.push(form.country);
                const locationString = locationParts.join(", ") || form.jobLocation || "";
                
                // Save requirements and create the job listing in the database
                const { data: insertedJob, error: jobError } = await supabase
                    .from('internal_job_listings')
                    .insert({
                        employer_id: user.id,
                        title: form.jobTitle,
                        company_name: form.companyName,
                        location: locationString,
                        employment_type: form.employmentType,
                        work_preference: form.workPreference,
                        specialty: form.specialty,
                        qualifications: form.qualifications,
                        experience_requirements: form.experienceRequirements,
                        requires_license: form.requiresLicense === 'yes',
                        sponsorship_available: form.sponsorship === 'yes',
                        salary_range: form.budgetForRole,
                        benefits: form.benefits,
                        job_description: form.about || form.roleDescription,
                        contact_first_name: form.firstName,
                        contact_last_name: form.lastName,
                        contact_email: form.workEmail,
                        company_url: form.companyUrl,
                        industry: form.industry,
                        company_description: form.about,
                        logo_filename: form.logoFileName,
                        // Social media links
                        social_media_links: validSocialMediaLinks,
                        // New fields from updated onboarding
                        role_description: form.roleDescription,
                        preferred_candidate: form.preferredCandidate,
                        duties_and_responsibilities: form.dutiesAndResponsibilities,
                        requirements_specialty: form.requirementsSpecialty,
                        experience_level: form.experienceLevel,
                        years_of_experience: form.yearsOfExperience ? Number(form.yearsOfExperience) : null,
                        budget_for_role: form.budgetForRole,
                        required_skills: form.requiredSkills,
                        other_preferences: form.otherPreferences,
                        country: form.country,
                        country_code: form.countryCode,
                        province: form.province,
                        language_requirement: form.languageRequirement,
                        location_or_licence_restrictions: form.locationOrLicenceRestrictions,
                        location_or_licence_restrictions_text: form.locationOrLicenceRestrictionsText,
                        // If screening questions is enabled but no questions added, treat as disabled
                        // This ensures users can enable screening questions without adding questions and complete without errors
                        screening_questions: effectiveScreeningQuestions,
                        screening_questions_list: effectiveScreeningQuestions ? screeningQuestionsList : null,
                        drivers_license_required: form.driversLicenseRequired,
                        drivers_license_answer: form.driversLicenseAnswer,
                        visa_sponsorship_offered: form.visaSponsorshipOffered,
                        visa_sponsorship_answer: form.visaSponsorshipAnswer,
                        status: 'published'
                    })
                    .select('id')
                    .single();

                if (jobError) throw jobError;
                
                // Explicitly ensure status is set to 'published' (in case database default or trigger overrides it)
                if (insertedJob?.id) {
                    const { error: updateStatusError } = await supabase
                        .from('internal_job_listings')
                        .update({ status: 'published' })
                        .eq('id', insertedJob.id)
                        .eq('employer_id', user.id);
                    
                    if (updateStatusError) {
                        console.warn('Failed to update job status to published:', updateStatusError);
                        // Don't throw - job was created successfully, status update is secondary
                    }
                }
                
                // Also save requirements to user metadata for profile info
                const employerProfile = {
                    ...(user?.user_metadata?.employer_profile || {}),
                    requirements: {
                        qualifications: form.qualifications,
                        experienceRequirements: form.experienceRequirements,
                        requiresLicense: form.requiresLicense,
                        sponsorship: form.sponsorship,
                        benefits: form.benefits,
                        requirementsSpecialty: form.requirementsSpecialty,
                        experienceLevel: form.experienceLevel,
                        yearsOfExperience: form.yearsOfExperience ? Number(form.yearsOfExperience) : null,
                        budgetForRole: form.budgetForRole,
                        salary_currency: form.salaryCurrency || "ZAR",
                        salary_period: form.salaryPeriod || "yearly",
                        salaryRange: form.budgetForRole, // Keep for backward compatibility
                        requiredSkills: form.requiredSkills,
                        otherPreferences: form.otherPreferences,
                        // If screening questions is enabled but no questions added, treat as disabled
                        // This ensures users can enable screening questions without adding questions and complete without errors
                        screeningQuestions: effectiveScreeningQuestions,
                        screeningQuestionsList: effectiveScreeningQuestions ? screeningQuestionsList : [],
                        driversLicenseRequired: form.driversLicenseRequired,
                        driversLicenseAnswer: form.driversLicenseAnswer,
                        visaSponsorshipOffered: form.visaSponsorshipOffered,
                        visaSponsorshipAnswer: form.visaSponsorshipAnswer,
                    }
                };
                
                const { error } = await supabase.auth.updateUser({
                    data: {
                        employer_profile: employerProfile,
                    },
                });
                if (error) throw error;
                
                // Update the database profile to mark onboarding as completed
                await updateProfileInfo({
                    onboardingCompleted: true,
                    onboardingStep: 3
                });
                
                showSuccess("Job listing created and profile completed! Welcome to DoctorsJobs");
                
                // Clear draft from localStorage since onboarding is complete
                if (user?.id) {
                    const draftKey = `employer_onboarding_draft_${user.id}`;
                    localStorage.removeItem(draftKey);
                }
                
                // Navigate to employer dashboard
                navigate("/employer/dashboard");
                return;
            }
            
            // Move to next step
            setCurrentStep((s) => Math.min(EMPLOYER_STEPS.length - 1, s + 1));
            
        } catch (error) {
            console.error("Error saving employer data:", error);
            showError(error.message || "Failed to save data. Please try again.");
        }
    };

    return (
        <>
        <style>{`
            /* Mobile-only styles - Desktop remains unchanged */
            @media (max-width: 767px) {
                /* Container padding - target by structure */
                div.min-h-screen[class*="bg-"] {
                    padding: 0.25rem !important;
                    padding-top: 0.5rem !important;
                    padding-bottom: 0.5rem !important;
                }
                /* Main content padding */
                section.flex-1 div[class*="bg-white"][class*="rounded-2xl"] {
                    padding: 0.75rem !important;
                }
                /* Headings */
                section.flex-1 div[class*="bg-white"] h1 {
                    font-size: 1.125rem !important;
                    margin-bottom: 0.75rem !important;
                    line-height: 1.5rem !important;
                }
                /* Form grids - force single column */
                div[class*="grid"][class*="grid-cols-1"] {
                    grid-template-columns: 1fr !important;
                    gap: 0.75rem !important;
                }
                /* Form inputs */
                div[class*="grid"][class*="grid-cols-1"] input,
                div[class*="grid"][class*="grid-cols-1"] select,
                div[class*="grid"][class*="grid-cols-1"] textarea {
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                    min-height: 44px !important;
                }
                /* Buttons */
                section.flex-1 button {
                    font-size: 0.875rem !important;
                    min-height: 44px !important;
                }
                /* Navigation buttons - stack vertically */
                div.flex.justify-between.mt-6 {
                    flex-direction: column-reverse !important;
                    gap: 0.625rem !important;
                    margin-top: 1.25rem !important;
                }
                div.flex.justify-between.mt-6 button {
                    width: 100% !important;
                }
                /* Header actions - stack on mobile */
                div.flex.items-center.justify-between.mb-3,
                div.flex.justify-between.items-center.mb-4 {
                    flex-direction: column !important;
                    align-items: stretch !important;
                    gap: 1rem !important;
                }
                div.flex.justify-between.items-center.mb-4 h1 {
                    width: 100% !important;
                }
                div.flex.justify-between.items-center.mb-4 button {
                    width: 100% !important;
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                }
                /* React Select mobile fixes */
                .react-select__control {
                    min-height: 44px !important;
                    font-size: 0.875rem !important;
                }
                .react-select__value-container {
                    padding: 0.375rem 0.625rem !important;
                }
                .react-select__input-container {
                    margin: 0 !important;
                }
                .react-select__menu {
                    font-size: 0.875rem !important;
                    z-index: 9999 !important;
                }
                .react-select__menu-portal {
                    z-index: 9999 !important;
                }
                /* Upload section - stack on mobile */
                div.flex.items-center.gap-4.mb-4 {
                    flex-direction: column !important;
                    gap: 1rem !important;
                }
                /* Social media inputs - stack on mobile */
                div.flex.gap-2.items-center {
                    flex-direction: column !important;
                    gap: 0.75rem !important;
                }
                div.flex.gap-2.items-center select,
                div.flex.gap-2.items-center input {
                    width: 100% !important;
                }
                /* Team member table - scrollable on mobile */
                div.overflow-x-auto {
                    -webkit-overflow-scrolling: touch !important;
                    margin: 0 -0.75rem !important;
                    padding: 0 0.75rem !important;
                }
                /* Modal adjustments */
                div.fixed.inset-0 div[class*="p-8"] {
                    padding: 1rem !important;
                }
                div.fixed.inset-0 h2 {
                    font-size: 1.25rem !important;
                }
                /* Ensure all interactive elements are touch-friendly */
                section.flex-1 input[type="checkbox"],
                section.flex-1 input[type="radio"] {
                    width: 20px !important;
                    height: 20px !important;
                    min-width: 20px !important;
                    min-height: 20px !important;
                }
                section.flex-1 textarea {
                    font-size: 0.875rem !important;
                    padding: 0.625rem !important;
                    min-height: 100px !important;
                }
                /* Step dropdown for mobile */
                div.xl\\:hidden select {
                    font-size: 0.875rem !important;
                    padding: 0.625rem 0.875rem !important;
                    min-height: 44px !important;
                }
            }
            /* Tablet styles - increased padding */
            @media (min-width: 768px) and (max-width: 1023px) {
                div.min-h-screen[class*="bg-"] {
                    padding: 1rem !important;
                    padding-top: 1.5rem !important;
                    padding-bottom: 1.5rem !important;
                }
                section.flex-1 div[class*="bg-white"][class*="rounded-2xl"] {
                    padding: 1.5rem !important;
                }
                div[class*="grid"][class*="grid-cols-1"] {
                    gap: 1rem !important;
                }
                div[class*="grid"][class*="grid-cols-1"] input,
                div[class*="grid"][class*="grid-cols-1"] select {
                    padding: 0.75rem 1rem !important;
                }
                section.flex-1 button {
                    padding: 0.75rem 1.5rem !important;
                }
                .react-select__value-container {
                    padding: 0.5rem 0.75rem !important;
                }
                section.flex-1 textarea {
                    padding: 0.75rem !important;
                }
            }
        `}</style>
        <div className="min-h-screen bg-[#f3f4f6] flex items-start justify-center py-6 px-4">
            <div className="w-full max-w-6xl rounded-2xl mx-auto px-4 md:px-0">
                <div className="flex flex-col xl:flex-row gap-4">
                    {/* Dropdown for steps on mobile & tablet (<1280px) */}
                    <div className="xl:hidden w-full mb-3">
                        <label className="block text-sm text-gray-700 mb-2">Company Profile</label>
                        <select
                            className="w-full rounded-full border border-gray-300 px-4 py-2"
                            value={currentStep}
                            onChange={(e) => setCurrentStep(parseInt(e.target.value, 10))}
                        >
                            {EMPLOYER_STEPS.map((label, idx) => (
                                <option key={label} value={idx}>
                                    {idx + 1}. {label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {/* Sidebar */}
                    <aside className="hidden xl:block xl:w-72 bg-white rounded-2xl shadow-sm border border-gray-200 p-4 h-max xl:mr-4">
                        <h3 className="text-gray-900 font-semibold mb-4">Company Profile</h3>
                        <ol className="space-y-4">
                            {EMPLOYER_STEPS.map((label, idx) => {
                                const isCurrent = idx === currentStep;
                                return (
                                    <li key={label} className="flex items-center gap-3 cursor-pointer" onClick={() => setCurrentStep(idx)}>
                                        <span className={`${isCurrent ? "font-semibold text-black" : "text-gray-500"}`}>{idx + 1}.</span>
                                        <span className={`${isCurrent ? "text-black font-medium" : "text-gray-600"}`}>{label}</span>
                                    </li>
                                );
                            })}
                        </ol>
                    </aside>

                    {/* Main content */}
                    <section className="flex-1 p-0">
                        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-6 xl:p-8">
                            <div className="flex items-center justify-between mb-3 text-gray-900">
                                <div className="flex items-center gap-2">
                                    <button aria-label="Go back" onClick={goBack} className="p-1">
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                                        </svg>
                                    </button>
                                    <span className="text-sm font-medium">
                                        Step {currentStep + 1} of {EMPLOYER_STEPS.length}
                                    </span>
                                </div>
                                <button
                                    onClick={handleSkip}
                                    className="text-sm text-black underline hover:text-gray-700"
                                >
                                    Skip for now
                                </button>
                            </div>
                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-3">
                                    <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">
                                        {currentStep === 0 && "Company Information"}
                                        {currentStep === 1 && "Team member"}
                                        {currentStep === 2 && "Job description"}
                                        {currentStep === 3 && "Requirements"}
                                    </h1>
                                {currentStep === 1 && (
                                    <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-lg text-sm font-medium">
                                        {countedTeamMembersCount}/{teamSeatLimit}
                                    </span>
                                )}
                            </div>
                            {currentStep === 1 && countedTeamMembersCount > 0 && (
                                <button
                                    onClick={addTeamMember}
                                    disabled={hasReachedSeatLimit}
                                    className={`bg-black text-white px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        hasReachedSeatLimit
                                            ? "opacity-50 cursor-not-allowed"
                                            : "hover:bg-gray-800"
                                    }`}
                                >
                                    Invite Team Members
                                </button>
                            )}
                            </div>

                            {currentStep === 1 && (
                                <div className="mb-6 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm font-semibold text-gray-900">Team collaboration seats</p>
                                            <p className="text-sm text-gray-600">
                                                {creditsLoading
                                                    ? "Checking your plan..."
                                                    : `${currentPlanLabel} includes ${teamSeatLimit} seat${teamSeatLimit === 1 ? "" : "s"} for collaborators.`}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-lg font-semibold text-gray-900">
                                                {countedTeamMembersCount} / {teamSeatLimit}
                                            </p>
                                            <p
                                                className={`text-sm ${hasReachedSeatLimit ? "text-gray-900" : "text-green-600"}`}
                                            >
                                                {hasReachedSeatLimit
                                                    ? "Seat limit reached"
                                                    : `${teamSeatLimit - countedTeamMembersCount} seat${teamSeatLimit - countedTeamMembersCount === 1 ? "" : "s"} available`}
                                            </p>
                                        </div>
                                    </div>
                                    {hasReachedSeatLimit && showUpgradeMessage && (
                                        <p className="mt-2 text-sm text-gray-900">
                                            {currentPlanLabel === "Free Starter" 
                                                ? "Upgrade to Sponsored Jobs to unlock up to 5 teammates."
                                                : "Upgrade to Enterprise to unlock more teammates."}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Upload logo - only show on company information step */}
                            {currentStep === 0 && (
                                <div className="flex items-center gap-4 mb-4">
                                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-700 overflow-hidden">
                                        {form.logoUrl ? (
                                            <img 
                                                src={form.logoUrl} 
                                                alt="Company Logo" 
                                                className="w-full h-full object-cover rounded-full"
                                            />
                                        ) : (
                                            <span>🏢</span>
                                        )}
                                    </div>
                                    <label className="inline-flex items-center gap-2 px-5 py-2 rounded-full border-2 border-gray-300 bg-white cursor-pointer text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                disabled={logoUploading}
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (file) {
                                                        handleLogoUpload(file);
                                                    }
                                                }}
                                            />
                                            <span>{logoUploading ? 'Uploading...' : 'Upload company Logo'}</span>
                                            {!logoUploading && (
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 16V4m0 0l-4 4m4-4l4 4M4 20h16" />
                                                </svg>
                                        )}
                                    </label>
                                    {form.logoFileName && <span className="text-sm text-gray-600">{form.logoFileName}</span>}
                                </div>
                            )}
                            {/* Step content */}
                            <Suspense fallback={<div className="flex justify-center items-center py-8"><div className="text-gray-500">Loading...</div></div>}>
                                {currentStep === 0 && (
                                    <EmployerOnboardingStep0
                                        form={form}
                                        setForm={setForm}
                                        socialMediaInputs={socialMediaInputs}
                                        updateSocialMediaInput={updateSocialMediaInput}
                                        addSocialMediaField={addSocialMediaField}
                                        removeSocialMediaField={removeSocialMediaField}
                                    />
                                )}
                                {currentStep === 1 && (
                                    <EmployerOnboardingStep1
                                        countedTeamMembersCount={countedTeamMembersCount}
                                        inviteForm={inviteForm}
                                        setInviteForm={setInviteForm}
                                        handleInviteSubmit={handleInviteSubmit}
                                        hasReachedSeatLimit={hasReachedSeatLimit}
                                        teamMembers={teamMembers}
                                        openEditModal={openEditModal}
                                        removeTeamMember={removeTeamMember}
                                    />
                                )}
                                {currentStep === 2 && (
                                    <EmployerOnboardingStep2
                                        form={form}
                                        setForm={setForm}
                                    />
                                )}
                                {currentStep === 3 && (
                                    <EmployerOnboardingStep3
                                        form={form}
                                        setForm={setForm}
                                        skillInput={skillInput}
                                        setSkillInput={setSkillInput}
                                        addSkill={addSkill}
                                        removeSkill={removeSkill}
                                        handleSelectScreening={handleSelectScreening}
                                        handleSelectDriversLicense={handleSelectDriversLicense}
                                        handleSelectVisaSponsorship={handleSelectVisaSponsorship}
                                        screeningQuestionsList={screeningQuestionsList}
                                        removeScreeningQuestion={removeScreeningQuestion}
                                        currentQuestion={currentQuestion}
                                        setCurrentQuestion={setCurrentQuestion}
                                        currentQuestionType={currentQuestionType}
                                        setCurrentQuestionType={setCurrentQuestionType}
                                        currentOptions={currentOptions}
                                        updateScreeningOption={updateScreeningOption}
                                        addScreeningOption={addScreeningOption}
                                        removeScreeningOption={removeScreeningOption}
                                        addScreeningQuestion={addScreeningQuestion}
                                        driversLicenseAnswer={driversLicenseAnswer}
                                        setDriversLicenseAnswer={setDriversLicenseAnswer}
                                        visaSponsorshipAnswer={visaSponsorshipAnswer}
                                        setVisaSponsorshipAnswer={setVisaSponsorshipAnswer}
                                        isCustomSpecialty={isCustomSpecialty}
                                        handleSpecialtySelect={handleSpecialtySelect}
                                        resetSpecialtyToSelect={resetSpecialtyToSelect}
                                    />
                                )}
                            </Suspense>

                            {/* Navigation buttons */}
                            <div className="flex justify-between mt-6">
                                <Button variant="text" onClick={goBack} className="!px-5 !py-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1 w-5 h-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" />
                                    </svg>
                                    Back
                                </Button>
                                <Button onClick={goNext} className="!px-6 !py-2">
                                    {currentStep === 3 ? "Complete" : "Continue"}
                                    {currentStep !== 3 && (
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ml-2 w-5 h-5">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                        </svg>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </section>
                </div>
            </div>

            {/* Invite Team Member Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-8">
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-2xl font-semibold text-gray-900">Invite Team Member</h2>
                                <button
                                    onClick={() => setShowInviteModal(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name*</label>
                                        <input 
                                            className="rounded-full border border-gray-300 px-4 py-3 w-full text-base" 
                                            value={inviteForm.firstName} 
                                            onChange={(e) => setInviteForm(prev => ({ ...prev, firstName: e.target.value }))} 
                                            placeholder="Bob" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Surname</label>
                                        <input 
                                            className="rounded-full border border-gray-300 px-4 py-3 w-full text-base" 
                                            value={inviteForm.lastName} 
                                            onChange={(e) => setInviteForm(prev => ({ ...prev, lastName: e.target.value }))} 
                                            placeholder="Smith"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Email Address*</label>
                                    <input 
                                        type="email" 
                                        className="rounded-full border border-gray-300 px-4 py-3 w-full text-base" 
                                        value={inviteForm.workEmail} 
                                        onChange={(e) => setInviteForm(prev => ({ ...prev, workEmail: e.target.value }))} 
                                        placeholder="bob@company.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Roles and permissions</label>
                                    <select
                                        className="rounded-full border border-gray-300 px-4 py-3 w-full text-base bg-white"
                                        value={inviteForm.role}
                                        onChange={(e) => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="member">Member</option>
                                        <option value="read-only">Read-only</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 mt-8">
                                <button
                                    onClick={() => setShowInviteModal(false)}
                                    className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleInviteSubmit}
                                    disabled={hasReachedSeatLimit}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                        hasReachedSeatLimit
                                            ? "bg-gray-400 text-white cursor-not-allowed"
                                            : "bg-black text-white hover:bg-gray-800"
                                    }`}
                                >
                                    Add Team Member
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Team Member Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-8">
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-2xl font-semibold text-gray-900">Edit Team Member</h2>
                                <button
                                    onClick={closeEditModal}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name*</label>
                                        <input 
                                            className="rounded-full border border-gray-300 px-4 py-3 w-full text-base" 
                                            value={editForm.firstName} 
                                            onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))} 
                                            placeholder="Bob" 
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Surname</label>
                                        <input 
                                            className="rounded-full border border-gray-300 px-4 py-3 w-full text-base" 
                                            value={editForm.lastName} 
                                            onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))} 
                                            placeholder="Smith"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Work Email Address*</label>
                                    <input 
                                        type="email" 
                                        className="rounded-full border border-gray-300 px-4 py-3 w-full text-base" 
                                        value={editForm.workEmail} 
                                        onChange={(e) => setEditForm(prev => ({ ...prev, workEmail: e.target.value }))} 
                                        placeholder="bob@company.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Roles and permissions</label>
                                    <select
                                        className="rounded-full border border-gray-300 px-4 py-3 w-full text-base bg-white"
                                        value={editForm.role}
                                        onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value }))}
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="member">Member</option>
                                        <option value="read-only">Read-only</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex justify-end gap-4 mt-8">
                                <button
                                    onClick={closeEditModal}
                                    className="px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleEditSubmit}
                                    className="bg-black text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        </>
    );
};

export default EmployerOnboardingPage;


